// Internal PnL helpers — used by the subgraph client to enrich open trades.
// Not exported from the SDK.

import {
  GetCurrentRolloverFee,
  GetTradeRolloverFee,
  GetTradeLiquidationPrice,
  GetEffectiveMaxLeverage,
  CalculateDynamicPriceImpact,
  CurrentTradeProfitRaw,
  CurrentTotalProfitRaw,
  CurrentTotalProfitP,
} from '@ostium/formulae';
import {
  formatUsdc,
  formatTokens,
  formatLeverage,
  parseUsdc,
  parseTokens,
  parseLeverage,
  getMaxLeverage,
  timeElapsedSince,
} from './decimal.js';
import type { RawPair, RawTrade } from './raw.js';
import type { PriceData } from '../types.js';

/** Internal PnL snapshot returned by `getTradePnL`. */
export interface TradePnL {
  /** Raw price PnL in USD before rollover. */
  pnl: number;
  /** PnL % after rollover, relative to collateral. */
  pnlPercent: number;
  /** Accrued rollover fee in USD. */
  rollover: number;
  /** Net PnL in USD after rollover. */
  netPnl: number;
  /** collateral + netPnl. */
  netValue: number;
  /** Estimated liquidation price. */
  liquidationPrice: number;
  /** Mid price after dynamic spread is applied. */
  priceAfterImpact: number;
}

export const EMPTY_PNL: TradePnL = {
  pnl: 0,
  pnlPercent: 0,
  rollover: 0,
  netPnl: 0,
  netValue: 0,
  liquidationPrice: 0,
  priceAfterImpact: 0,
};

export function getRolloverAccumulator(
  pair: RawPair,
  isBuy: boolean,
  blockNumber: bigint | string,
): string {
  const accRollover = isBuy
    ? (pair.accRolloverLong ?? '0')
    : (pair.accRolloverShort ?? '0');

  return GetCurrentRolloverFee(
    accRollover,
    pair.lastRolloverBlock     ?? '0',
    pair.brokerPremium         ?? '0',
    pair.lastRolloverLongPure  ?? '0',
    isBuy,
    pair.isNegativeRolloverAllowed ?? false,
    blockNumber.toString(),
  );
}

function getTradeRolloverFeeRaw(trade: RawTrade, currentRolloverAccumulator: string): bigint {
  return BigInt(
    GetTradeRolloverFee(
      trade.rollover,
      currentRolloverAccumulator,
      trade.collateral,
      trade.leverage,
    ),
  );
}

function getLiquidationPrice(trade: RawTrade, rolloverRaw: bigint): number {
  if (!trade.leverage   || trade.leverage   === '0') return 0;
  if (!trade.openPrice  || trade.openPrice  === '0') return 0;
  if (!trade.collateral || trade.collateral === '0') return 0;

  try {
    const maxLeverage = GetEffectiveMaxLeverage(
      getMaxLeverage(trade),
      trade.pair.overnightMaxLeverage ?? '0',
      trade.isDayTrade ?? false,
    );

    return formatTokens(
      GetTradeLiquidationPrice(
        trade.openPrice,
        trade.isBuy,
        trade.collateral,
        trade.leverage,
        rolloverRaw.toString(),
        '0', // funding excluded
        maxLeverage,
      ),
    );
  } catch {
    return 0;
  }
}

function getPriceImpact(
  marketPrice: PriceData,
  isBuy: boolean,
  isOpen: boolean,
  pair: RawPair,
  collateral: number,
  leverage: number,
): { priceImpactP: string; priceAfterImpact: string } {
  if (!marketPrice.mid) return { priceImpactP: '0', priceAfterImpact: '0' };

  return CalculateDynamicPriceImpact(
    parseTokens(marketPrice.mid).toString(),
    parseTokens(marketPrice.ask).toString(),
    parseTokens(marketPrice.bid).toString(),
    isOpen,
    isBuy,
    parseUsdc(collateral).toString(),
    parseLeverage(leverage).toString(),
    pair.buyVolume       ?? '0',
    pair.sellVolume      ?? '0',
    pair.decayRate       ?? '0',
    timeElapsedSince(pair),
    pair.netVolThreshold ?? '0',
    pair.priceImpactK    ?? '0',
  );
}

/**
 * Full PnL snapshot for an open trade at the current block.
 * Rollover is the only ongoing fee included (funding excluded by design).
 */
export function getTradePnL(
  trade: RawTrade,
  marketPrice: PriceData,
  blockNumber: bigint,
): TradePnL {
  if (!trade?.pair || !marketPrice?.mid || !blockNumber) return EMPTY_PNL;

  const rolloverAcc = getRolloverAccumulator(trade.pair, trade.isBuy, blockNumber);
  const rolloverRaw = getTradeRolloverFeeRaw(trade, rolloverAcc);

  const liquidationPrice = getLiquidationPrice(trade, rolloverRaw);

  // Price impact (display only — PnL uses mid to avoid scary spread PnL).
  const impactResult = getPriceImpact(
    marketPrice,
    trade.isBuy,
    false,
    trade.pair,
    formatUsdc(trade.collateral),
    formatLeverage(trade.leverage),
  );

  const priceForPnL = parseTokens(marketPrice.mid).toString();

  const pnlRaw = BigInt(
    CurrentTradeProfitRaw(
      trade.openPrice,
      priceForPnL,
      trade.isBuy,
      trade.leverage,
      trade.highestLeverage,
      trade.collateral,
    ),
  );

  const totalProfitRaw = BigInt(
    CurrentTotalProfitRaw(
      trade.openPrice,
      priceForPnL,
      trade.isBuy,
      trade.leverage,
      trade.highestLeverage,
      trade.collateral,
      rolloverRaw.toString(),
      '0', // funding excluded
    ),
  );

  const pnlPercentRaw = BigInt(
    CurrentTotalProfitP(totalProfitRaw.toString(), trade.collateral),
  );

  return {
    pnl:              formatUsdc(pnlRaw),
    pnlPercent:       formatUsdc(pnlPercentRaw),
    rollover:         formatUsdc(rolloverRaw),
    netPnl:           formatUsdc(totalProfitRaw),
    netValue:         formatUsdc(totalProfitRaw) + formatUsdc(trade.collateral),
    liquidationPrice,
    priceAfterImpact: formatTokens(impactResult.priceAfterImpact),
  };
}
